// Lib
import { clamp } from 'lodash';

/** Number of keyboard zoom presses between 100% and the max scale (shared by canvas and image modal). */
export const ZOOM_FOCAL_LENGTH_STEP_COUNT = 4;

/**
 * Focal length for stepping between 100% and a max scale above 100%.
 * Formula: scale = focalLength / (focalLength - index)
 */
export const getFocalLengthForZoomIn = (
    maxScale: number,
    stepCount = ZOOM_FOCAL_LENGTH_STEP_COUNT,
): number => (maxScale * stepCount) / (maxScale - 1);

/**
 * Focal length for stepping between a min scale below 100% and 100%.
 */
export const getFocalLengthForZoomOut = (
    minScale: number,
    stepCount = ZOOM_FOCAL_LENGTH_STEP_COUNT,
): number => -(minScale * stepCount) / (minScale - 1);

/**
 * Determines the current step index for a scale on the focal-length curve.
 */
export const getFocalLengthStepIndex = (currentScale: number, focalLength: number): number => {
    const index = focalLength - focalLength / currentScale;
    // If the index is really close to the higher number, use the higher number.
    if (index % 1 > 0.99 || (index % 1 < 0 && index % 1 > -0.001)) return Math.ceil(index);
    return Math.floor(index);
};

/**
 * Returns the scale for a step index on the focal-length curve.
 */
export const getScaleFromFocalLengthStepIndex = (index: number, focalLength: number): number =>
    focalLength / (focalLength - index);

/**
 * Steps the scale forwards or backwards along a focal-length curve and clamps to bounds.
 */
export const getNextScaleFromFocalLengthStep = ({
    currentScale,
    focalLength,
    stepChange,
    minScale,
    maxScale,
}: {
    currentScale: number;
    focalLength: number;
    stepChange: 1 | -1;
    minScale: number;
    maxScale: number;
}): number => {
    const currentIndex = getFocalLengthStepIndex(currentScale, focalLength);
    const nextIndex = currentIndex + stepChange;
    const nextScale = getScaleFromFocalLengthStepIndex(nextIndex, focalLength);

    return clamp(nextScale, minScale, maxScale);
};
